import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Box,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Snackbar
} from '@mui/material';

// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import StorageIcon from '@mui/icons-material/Storage';
import CachedIcon from '@mui/icons-material/Cached';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import BugReportIcon from '@mui/icons-material/BugReport';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsIcon from '@mui/icons-material/Notifications';

import axios from 'axios';
import lssLogo from '../lsslogo.png';
import lgaLogo from '../lgalogo.png';
import io from 'socket.io-client';

// API base URL
const API_BASE_URL = 'http://localhost:5050/api/test-sheet-submissions';

interface TestSheetSubmission {
  id: string;
  account_email: string;
  sender_name: string;
  sender_email: string;
  subject: string;
  date: string;
  timestamp: number;
  body_preview: string;
  attachments: TestSheetAttachment[];
  attachment_count: number;
  thread_id: string;
  snippet: string;
  submission_type: string;
  has_attachments: boolean;
  attachment_types: string[];
  total_attachment_size: number;
  tags: string[];
}

interface TestSheetAttachment {
  filename: string;
  mime_type: string;
  size: number;
  size_formatted: string;
  attachment_id: string;
  file_type: string;
  is_test_sheet: boolean;
}

interface ThreadMessage {
  id: string;
  thread_id: string;
  account_email: string;
  sender_name: string;
  sender_email: string;
  to_recipients?: string[];
  cc_recipients?: string[];
  bcc_recipients?: string[];
  subject: string;
  date: string;
  timestamp: number;
  body_text: string;
  body_html: string;
  snippet: string;
  attachments: TestSheetAttachment[];
  has_attachments: boolean;
}

interface SubmissionData {
  submissions: TestSheetSubmission[];
  total_count: number;
  search_period_days: number;
  last_updated: string;
  account_status: Record<string, any>;
  monitored_accounts: string[];
  from_cache?: boolean;
  cache_expires_at?: string;
  stale_cache?: boolean;
  error?: string;
}

interface Statistics {
  total_submissions: number;
  total_attachments: number;
  recent_submissions_7_days: number;
  average_attachments_per_submission: number;
  submission_types: Record<string, number>;
  submissions_by_account: Record<string, number>;
  search_period_days: number;
  generated_at: string;
}

interface HealthData {
  total_accounts: number;
  connected_accounts: number;
  health_percentage: number;
  account_details: Record<string, any>;
  last_checked: string;
}

interface CacheStatus {
  cached_entries: number;
  cache_entries: string[];
  cache_expiry_times: Record<string, string>;
  cache_duration_minutes: number;
  timestamp: string;
}

interface Filters {
  daysBack: number;
  senderEmail: string;
  subjectContains: string;
  submissionType: string;
  hasAttachments: boolean | null;
  actionRequired: boolean | null;
}

interface ThreadGroup {
  thread_id: string;
  subject: string;
  participants: string[];
  message_count: number;
  latest_date: string;
  latest_timestamp: number;
  earliest_date: string;
  earliest_timestamp: number;
  total_attachments: number;
  has_attachments: boolean;
  submission_types: string[];
  account_emails: string[];
  messages: TestSheetSubmission[];
  tags: string[];
}

const TestSheetSubmissions: React.FC = () => {
  // Ref to store loadSubmissions function for use in useEffect without dependency issues
  const loadSubmissionsRef = useRef<((forceRefresh?: boolean) => Promise<void>) | null>(null);

  // State for submissions data
  const [submissions, setSubmissions] = useState<TestSheetSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchPeriodDays, setSearchPeriodDays] = useState(30);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [isFromCache, setIsFromCache] = useState(false);

  // State for additional data
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null);

  // State for UI
  const [selectedSubmission, setSelectedSubmission] = useState<TestSheetSubmission | null>(null);
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<TestSheetAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sortBy, setSortBy] = useState<keyof TestSheetSubmission>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Real-time notifications
  const [realtimeNotification, setRealtimeNotification] = useState<string | null>(null);
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [lastPollingCheck, setLastPollingCheck] = useState<string>('');
  
  // UI state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Filters
  const [filters, setFilters] = useState<Filters>({
    daysBack: 30,
    senderEmail: '',
    subjectContains: '',
    submissionType: '',
    hasAttachments: null,
    actionRequired: null
  });

  // Initialize WebSocket connection
  useEffect(() => {
    const socketConnection = io('http://localhost:5050');

    socketConnection.on('connect', () => {
      console.log('Connected to real-time updates');
    });

    socketConnection.on('test_sheet_update', (data) => {
      console.log('Received real-time update:', data);
      
      if (data.type === 'new_emails') {
        setRealtimeNotification(`New emails received: ${data.data.message}`);
        // Auto-refresh data after a short delay
        setTimeout(() => {
          loadSubmissionsRef.current?.(false);
        }, 2000);
      }
    });

    socketConnection.on('disconnect', () => {
      console.log('Disconnected from real-time updates');
    });

    return () => {
      socketConnection.disconnect();
    };
  }, []);

  // Clear notification after 5 seconds
  useEffect(() => {
    if (realtimeNotification) {
      const timer = setTimeout(() => {
        setRealtimeNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [realtimeNotification]);

  // Polling for new emails (alternative to WebSocket)
  useEffect(() => {
    if (!pollingEnabled) return;

    const pollForUpdates = async () => {
      try {
        // Check if there are new emails by comparing timestamps
        const response = await axios.get(`${API_BASE_URL}/submissions?days_back=1&force_refresh=false`);
        const data = response.data;
        
        if (data.last_updated && lastPollingCheck && data.last_updated > lastPollingCheck) {
          setRealtimeNotification('New emails detected - refreshing data...');
          await loadSubmissionsRef.current?.(false);
        }
        
        setLastPollingCheck(new Date().toISOString());
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Poll every 30 seconds
    const interval = setInterval(pollForUpdates, 30000);
    
    return () => clearInterval(interval);
  }, [pollingEnabled, lastPollingCheck]);

  // Toggle polling
  const togglePolling = () => {
    setPollingEnabled(!pollingEnabled);
    if (!pollingEnabled) {
      setLastPollingCheck(new Date().toISOString());
      setRealtimeNotification('Real-time polling enabled');
    } else {
      setRealtimeNotification('Real-time polling disabled');
    }
  };

  // Load submissions
  const loadSubmissions = useCallback(async (forceRefresh: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        days_back: filters.daysBack.toString(),
        force_refresh: forceRefresh.toString(),
      });

      // Increase timeout for large date ranges
      const timeout = filters.daysBack > 90 ? 60000 : 30000; // 60s for large ranges, 30s for normal
      
      const response = await axios.get(`${API_BASE_URL}/submissions?${params}`, {
        timeout: timeout
      });

      const data: SubmissionData = response.data;
      setSubmissions(data.submissions);
      setTotalCount(data.total_count);
      setSearchPeriodDays(data.search_period_days);
      setLastUpdated(data.last_updated);
      setIsFromCache(data.from_cache || false);

    } catch (err: any) {
      if (err.code === 'ECONNABORTED') {
        setError(`Request timed out. Large date ranges (${filters.daysBack} days) may take longer to process. Try using cache or reducing the date range.`);
      } else {
        setError(err.response?.data?.error || 'Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  }, [filters.daysBack]);

  // Store loadSubmissions in ref for use in useEffect without dependency issues
  loadSubmissionsRef.current = loadSubmissions;

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/submissions/statistics?days_back=${filters.daysBack}`);
      setStatistics(response.data);
    } catch (err: any) {
      console.error('Failed to load statistics:', err);
    }
  }, [filters.daysBack]);

  // Load health data
  const loadHealthData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/accounts/health`);
      setHealthData(response.data);
    } catch (err: any) {
      console.error('Failed to load health data:', err);
    }
  }, []);

  // Load cache status
  const loadCacheStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/cache/status`);
      setCacheStatus(response.data);
    } catch (err: any) {
      console.error('Failed to load cache status:', err);
    }
  }, []);

  // Warm cache
  const warmCache = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/cache/warm`, {
        days_back: filters.daysBack
      });
      
      // Reload data after warming cache
      await loadSubmissions(false);
      await loadStatistics();
      await loadCacheStatus();
      
      setError(null);
    } catch (err: any) {
      setError(`Failed to warm cache: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Full historical sync
  const [historicalSyncing, setHistoricalSyncing] = useState<boolean>(false);
  
  const syncFullHistory = async (years: number = 5) => {
    setHistoricalSyncing(true);
    setError(null);
    
    try {
      // Show immediate feedback
      setError(`🔄 Starting full historical sync for ${years} years... This may take several minutes.`);
      
      const response = await axios.post(`${API_BASE_URL}/sync/full-history`, {
        years_back: years
      }, {
        timeout: 600000  // 10 minute timeout for large historical sync
      });
      
      // Show success message
      setError(`✅ ${response.data.message}. Synced ${response.data.sync_result?.new_synced || 0} new emails.`);
      
      // Reload data after sync
      await loadSubmissions(false);
      await loadStatistics();
      
      // Clear success message after 10 seconds
      setTimeout(() => setError(null), 10000);
      
    } catch (err: any) {
      if (err.code === 'ECONNABORTED') {
        setError(`⏰ Historical sync is still running in the background. This may take 10-20 minutes for large datasets. Check back later.`);
      } else {
        setError(`Failed to sync full history: ${err.response?.data?.error || err.message}`);
      }
    } finally {
      setHistoricalSyncing(false);
    }
  };

  // Auto-sync new emails (for regular maintenance)
  const autoSyncNew = useCallback(async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/sync/auto-new`);
      
      // Reload data after sync
      await loadSubmissions(false);
      await loadStatistics();
      
      // Show brief success message
      const newCount = response.data.sync_result?.new_synced || 0;
      if (newCount > 0) {
        setError(`✅ Auto-sync complete: ${newCount} new emails added.`);
        setTimeout(() => setError(null), 5000);
      }
      
    } catch (err: any) {
      console.error('Auto-sync failed:', err);
      // Don't show error for auto-sync failures (silent background operation)
    }
  }, [loadSubmissions, loadStatistics]);

  // Reset connections to resolve SSL issues
  const resetConnections = async () => {
    try {
      setError(null);
      const response = await axios.post(`${API_BASE_URL}/connections/reset`);
      
      // Reload all data after resetting connections
      await loadHealthData();
      await loadSubmissions(false);
      await loadStatistics();
      
      // Show success message briefly
      setError(`✅ ${response.data.message}`);
      setTimeout(() => setError(null), 3000);
      
    } catch (err: any) {
      setError(`Failed to reset connections: ${err.response?.data?.error || err.message}`);
    }
  };

  // Update action required tags
  const updateTags = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/tags/update`, {}, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      setError(`Updated tags for ${response.data.threads_updated} threads`);
      // Refresh data to show updated tags
      await loadSubmissions();
    } catch (error: any) {
      console.error('Error updating tags:', error);
      setError(`Error updating tags: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const autoUpdateAllTags = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/tags/auto-update`, {}, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      setError(`Auto-updated tags for ${response.data.threads_updated} threads`);
      // Refresh data to show updated tags
      await loadSubmissions();
    } catch (error: any) {
      console.error('Error auto-updating tags:', error);
      setError(`Error auto-updating tags: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Search with filters
  const searchSubmissions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/submissions/search`, filters);

      const data = response.data;
      setSubmissions(data.submissions);
      setTotalCount(data.total_count);
      setLastUpdated(data.last_updated);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to search submissions');
    } finally {
      setLoading(false);
    }
  };

  // Download attachment
  const downloadAttachment = async (submission: TestSheetSubmission, attachment: TestSheetAttachment) => {
    try {
      const params = new URLSearchParams({
        account_email: submission.account_email,
        filename: attachment.filename,
      });

      const response = await axios.get(
        `${API_BASE_URL}/attachments/${submission.id}/${attachment.attachment_id}?${params}`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err: any) {
      setError(`Failed to download attachment: ${err.response?.data?.error || err.message}`);
    }
  };

  // Load thread messages
  const loadThreadMessages = async (submission: TestSheetSubmission) => {
    if (!submission.thread_id) {
      setThreadMessages([]);
      return;
    }

    setLoadingThread(true);
    try {
      const params = new URLSearchParams({
        account_email: submission.account_email
      });
      const response = await axios.get(`${API_BASE_URL}/threads/${submission.thread_id}?${params}`);
      setThreadMessages(response.data.emails || []);
    } catch (error) {
      console.error('Error loading thread:', error);
      setError('Failed to load email thread');
      setThreadMessages([]);
    } finally {
      setLoadingThread(false);
    }
  };

  // Preview attachment
  const previewAttachmentFile = async (submission: TestSheetSubmission, attachment: TestSheetAttachment) => {
    setPreviewAttachment(attachment);
    setPreviewDialogOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      const params = new URLSearchParams({
        account_email: submission.account_email,
        filename: attachment.filename,
      });

      const response = await axios.get(
        `${API_BASE_URL}/attachments/${submission.id}/${attachment.attachment_id}?${params}`,
        {
          responseType: 'blob',
        }
      );

      // Create a blob URL for preview
      const blob = new Blob([response.data], { type: attachment.mime_type });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err: any) {
      setError(`Failed to load attachment preview: ${err.response?.data?.error || err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Close preview and cleanup
  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewDialogOpen(false);
    setPreviewAttachment(null);
    setPreviewUrl(null);
    setPreviewLoading(false);
  };

  // Handle sort
  const handleSort = (column: keyof TestSheetSubmission) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  // Filter out confirmation receipt emails and group submissions by thread
  const threadGroups = React.useMemo(() => {
    const groups = new Map<string, ThreadGroup>();
    
    // Filter out emails with "Confirmation Receipt" in subject line
    const filteredSubmissions = submissions.filter(submission => 
      !submission.subject.toLowerCase().includes('confirmation receipt')
    );
    
    filteredSubmissions.forEach(submission => {
      const threadId = submission.thread_id || submission.id; // Fallback to message ID if no thread_id
      
      if (!groups.has(threadId)) {
        groups.set(threadId, {
          thread_id: threadId,
          subject: submission.subject,
          participants: [],
          message_count: 0,
          latest_date: submission.date,
          latest_timestamp: submission.timestamp,
          earliest_date: submission.date,
          earliest_timestamp: submission.timestamp,
          total_attachments: 0,
          has_attachments: false,
          submission_types: [],
          account_emails: [],
          messages: [],
          tags: []
        });
      }
      
      const group = groups.get(threadId)!;
      group.messages.push(submission);
      group.message_count = group.messages.length;
      
      // Update latest/earliest dates
      if (submission.timestamp > group.latest_timestamp) {
        group.latest_date = submission.date;
        group.latest_timestamp = submission.timestamp;
      }
      if (submission.timestamp < group.earliest_timestamp) {
        group.earliest_date = submission.date;
        group.earliest_timestamp = submission.timestamp;
      }
      
      // Update participants (unique sender emails)
      if (!group.participants.includes(submission.sender_email)) {
        group.participants.push(submission.sender_email);
      }
      
      // Update account emails (unique account emails)
      if (!group.account_emails.includes(submission.account_email)) {
        group.account_emails.push(submission.account_email);
      }
      
      // Update submission types (unique)
      if (!group.submission_types.includes(submission.submission_type)) {
        group.submission_types.push(submission.submission_type);
      }
      
      // Update attachments
      group.total_attachments += submission.attachment_count;
      if (submission.has_attachments) {
        group.has_attachments = true;
      }
      
      // Update tags (unique)
      submission.tags.forEach(tag => {
        if (!group.tags.includes(tag)) {
          group.tags.push(tag);
        }
      });
    });
    
    return Array.from(groups.values());
  }, [submissions]);

  // Apply action required filter to thread groups
  const filteredThreadGroups = React.useMemo(() => {
    if (filters.actionRequired === null) {
      return threadGroups; // No filter applied
    }
    
    return threadGroups.filter(threadGroup => {
      const hasActionRequired = threadGroup.tags.includes('action-required');
      return filters.actionRequired ? hasActionRequired : !hasActionRequired;
    });
  }, [threadGroups, filters.actionRequired]);

  // Sort thread groups
  const sortedThreadGroups = React.useMemo(() => {
    return [...filteredThreadGroups].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Map sort keys to thread group properties
      switch (sortBy) {
        case 'date':
          aValue = a.latest_timestamp;
          bValue = b.latest_timestamp;
          break;
        case 'sender_name':
          // Sort by first participant
          aValue = a.participants[0] || '';
          bValue = b.participants[0] || '';
          break;
        case 'subject':
          aValue = a.subject;
          bValue = b.subject;
          break;
        default:
          // For other fields, use the latest message in the thread
          const latestA = a.messages.reduce((latest, msg) => 
            msg.timestamp > latest.timestamp ? msg : latest
          );
          const latestB = b.messages.reduce((latest, msg) => 
            msg.timestamp > latest.timestamp ? msg : latest
          );
          aValue = latestA[sortBy];
          bValue = latestB[sortBy];
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredThreadGroups, sortBy, sortDirection]);

  // Get file type icon
  const getFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <PictureAsPdfIcon color="error" />;
      case 'word document':
        return <DescriptionIcon color="primary" />;
      case 'image':
        return <ImageIcon color="success" />;
      case 'archive':
        return <FolderZipIcon color="warning" />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  // Get submission type color
  const getSubmissionTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type.toLowerCase()) {
      case 'test sheet':
        return 'primary';
      case 'answer sheet':
        return 'success';
      case 'practical assessment':
        return 'warning';
      case 'written test':
        return 'info';
      default:
        return 'default';
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Clean up sender display names
  const getCleanSenderName = (senderName: string, senderEmail: string) => {
    // Check sender name first
    if (senderName) {
      if (senderName.toLowerCase().includes('test sheets lga') || senderName.toLowerCase().includes('test_sheets_lga')) {
        return 'LGA';
      }
      if (senderName.toLowerCase().includes('test sheets') || senderName.toLowerCase().includes('test_sheets')) {
        return 'LSS';
      }
    }
    
    // Check sender email if name doesn't match
    if (senderEmail) {
      if (senderEmail.toLowerCase().includes('testsheets@lifeguardingacademy.com')) {
        return 'LGA';
      }
      if (senderEmail.toLowerCase().includes('test_sheets@lifeguarding.com')) {
        return 'LSS';
      }
      if (senderEmail.toLowerCase().includes('@lifeguardingacademy.com')) {
        return 'LGA';
      }
      if (senderEmail.toLowerCase().includes('@lifeguarding.com')) {
        return 'LSS';
      }
    }
    
    // Fallback to original name or email
    return senderName || senderEmail || 'Unknown';
  };

  // Format recipient list for display
  const formatRecipientList = (recipients: string[] | undefined, maxDisplay: number = 2) => {
    if (!recipients || recipients.length === 0) {
      return null;
    }
    
    if (recipients.length <= maxDisplay) {
      return recipients.join(', ');
    }
    
    return `${recipients.slice(0, maxDisplay).join(', ')} +${recipients.length - maxDisplay} more`;
  };

  // Get organization logo based on sender
  const getOrganizationLogo = (senderName: string, senderEmail: string) => {
    const cleanName = getCleanSenderName(senderName, senderEmail);
    
    if (cleanName === 'LGA') {
      return lgaLogo;
    } else if (cleanName === 'LSS') {
      return lssLogo;
    }
    
    return null; // No logo for external senders
  };

  const getTagProps = (tag: string) => {
    // Special styling for action-required tag
    if (tag === 'action-required') {
      return {
        label: '⚠️ Action Required',
        color: 'error' as const,
        variant: 'filled' as const,
        sx: { 
          fontWeight: 'bold',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.7 },
            '100%': { opacity: 1 }
          }
        }
      };
    }
    
    // Special styling for urgent tag
    if (tag === 'urgent') {
      return {
        label: '🚨 Urgent',
        color: 'warning' as const,
        variant: 'filled' as const,
        sx: { fontWeight: 'bold' }
      };
    }
    
    // Default styling for other tags
    return {
      label: tag,
      color: 'default' as const,
      variant: 'outlined' as const,
      sx: {}
    };
  };

  // Render attachment preview content
  const renderAttachmentPreview = () => {
    if (!previewAttachment || !previewUrl) return null;

    const fileType = previewAttachment.file_type.toLowerCase();
    const mimeType = previewAttachment.mime_type.toLowerCase();

    // PDF Preview
    if (fileType === 'pdf' || mimeType.includes('pdf')) {
      return (
        <Box sx={{ width: '100%', height: '70vh' }}>
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title={previewAttachment.filename}
          />
        </Box>
      );
    }

    // Image Preview
    if (fileType === 'image' || mimeType.startsWith('image/')) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <img
            src={previewUrl}
            alt={previewAttachment.filename}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '70vh', 
              objectFit: 'contain' 
            }}
          />
        </Box>
      );
    }

    // Text file preview
    if (mimeType.startsWith('text/') || fileType === 'text') {
      return (
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: '70vh', overflow: 'auto' }}>
          <iframe
            src={previewUrl}
            width="100%"
            height="400px"
            style={{ border: 'none' }}
            title={previewAttachment.filename}
          />
        </Box>
      );
    }

    // Fallback for other file types
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom>
          Preview not available
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          This file type ({previewAttachment.file_type}) cannot be previewed in the browser.
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => {
            if (previewUrl) {
              const link = document.createElement('a');
              link.href = previewUrl;
              link.download = previewAttachment.filename;
              link.click();
            }
          }}
        >
          Download File
        </Button>
      </Box>
    );
  };

  // Load data on component mount
  useEffect(() => {
    loadSubmissions();
    loadStatistics();
    loadHealthData();
    loadCacheStatus();
  }, [loadSubmissions, loadStatistics, loadHealthData, loadCacheStatus]);

  // Keyboard shortcut to toggle admin panel (Ctrl+Shift+A)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        setShowAdminPanel(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-sync new emails every 30 minutes
  useEffect(() => {
    const autoSyncInterval = setInterval(() => {
      console.log('Running automatic email sync...');
      autoSyncNew();
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(autoSyncInterval);
  }, [autoSyncNew]);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header and Controls Section with Seaglass Background */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, rgba(158, 217, 217, 0.15) 0%, rgba(144, 238, 144, 0.1) 25%, rgba(176, 224, 230, 0.15) 50%, rgba(152, 251, 152, 0.1) 75%, rgba(175, 238, 238, 0.15) 100%)',
          borderRadius: 3,
          p: 3,
          mb: 4,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Header */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Test Sheet Submissions
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Monitor test sheet submissions sent to and from test_sheets@lifeguarding.com
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowSettings(!showSettings)}
              sx={{ mt: 1 }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>

          {/* Health and Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Health Status Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(5px)' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <HealthAndSafetyIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Account Health</Typography>
                  </Box>
                  {healthData ? (
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h4" color={healthData.health_percentage > 80 ? 'success.main' : 'error.main'}>
                        {Math.round(healthData.health_percentage)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {healthData.connected_accounts} of {healthData.total_accounts} accounts connected
                      </Typography>
                      {healthData.health_percentage < 100 && (
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            size="small" 
                            label="Connection Issues" 
                            color="warning" 
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Try "Reset Connections" if SSL errors persist
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Statistics Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(5px)' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Statistics</Typography>
                  </Box>
                  {statistics ? (
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h4" color="primary">
                        {statistics.total_submissions}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total submissions ({statistics.search_period_days} days)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {statistics.recent_submissions_7_days} recent (7 days)
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Attachments Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(5px)' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AttachFileIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Attachments</Typography>
                  </Box>
                  {statistics ? (
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h4" color="secondary">
                        {statistics.total_attachments}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total attachments
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg: {statistics.average_attachments_per_submission.toFixed(1)} per submission
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search Bar */}
          <Box>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Search by Subject Line"
                  placeholder="Enter keywords to search in subject lines..."
                  value={filters.subjectContains}
                  onChange={(e) => setFilters(prev => ({ ...prev, subjectContains: e.target.value }))}
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(5px)'
                    }
                  }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: filters.subjectContains && (
                      <IconButton
                        size="small"
                        onClick={() => setFilters(prev => ({ ...prev, subjectContains: '' }))}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      searchSubmissions();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={searchSubmissions}
                  disabled={loading}
                  size="small"
                  fullWidth
                  sx={{ height: '40px' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Settings Panel */}
        {showSettings && (
          <Accordion expanded sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Search Period"
                    value={filters.daysBack}
                    onChange={(e) => setFilters(prev => ({ ...prev, daysBack: parseInt(e.target.value) }))}
                    size="small"
                    helperText={filters.daysBack > 90 ? "Large ranges may take longer" : ""}
                  >
                    <MenuItem value={7}>Last 7 days</MenuItem>
                    <MenuItem value={30}>Last 30 days</MenuItem>
                    <MenuItem value={60}>Last 60 days</MenuItem>
                    <MenuItem value={90}>Last 90 days</MenuItem>
                    <MenuItem value={180}>Last 6 months ⚠️</MenuItem>
                    <MenuItem value={365}>Last year ⚠️</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    size="small"
                    fullWidth
                  >
                    {showFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
                  </Button>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadSubmissions(true)}
                    disabled={loading}
                    size="small"
                    fullWidth
                  >
                    Force Refresh
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Tooltip title="Admin Panel (Ctrl+Shift+A)" arrow>
                    <Button
                      variant={showAdminPanel ? "contained" : "outlined"}
                      color={showAdminPanel ? "warning" : "primary"}
                      startIcon={<SettingsIcon />}
                      onClick={() => setShowAdminPanel(!showAdminPanel)}
                      size="small"
                      fullWidth
                    >
                      {showAdminPanel ? 'Hide Admin Panel' : 'Show Admin Panel'}
                    </Button>
                  </Tooltip>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Filters */}
        {showFilters && (
          <Accordion expanded sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Search Filters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sender Email"
                    value={filters.senderEmail}
                    onChange={(e) => setFilters(prev => ({ ...prev, senderEmail: e.target.value }))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Subject Contains"
                    value={filters.subjectContains}
                    onChange={(e) => setFilters(prev => ({ ...prev, subjectContains: e.target.value }))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Submission Type</InputLabel>
                    <Select
                      value={filters.submissionType}
                      onChange={(e) => setFilters(prev => ({ ...prev, submissionType: e.target.value }))}
                      label="Submission Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="Test Sheet">Test Sheet</MenuItem>
                      <MenuItem value="Answer Sheet">Answer Sheet</MenuItem>
                      <MenuItem value="Practical Assessment">Practical Assessment</MenuItem>
                      <MenuItem value="Written Test">Written Test</MenuItem>
                      <MenuItem value="PDF Submission">PDF Submission</MenuItem>
                      <MenuItem value="Document Submission">Document Submission</MenuItem>
                      <MenuItem value="General Submission">General Submission</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Has Attachments</InputLabel>
                    <Select
                      value={filters.hasAttachments === null ? 'all' : filters.hasAttachments.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilters(prev => ({
                          ...prev,
                          hasAttachments: value === 'all' ? null : value === 'true'
                        }));
                      }}
                      label="Has Attachments"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="true">With Attachments</MenuItem>
                      <MenuItem value="false">No Attachments</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action Required</InputLabel>
                    <Select
                      value={filters.actionRequired === null ? 'all' : filters.actionRequired.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilters(prev => ({
                          ...prev,
                          actionRequired: value === 'all' ? null : value === 'true'
                        }));
                      }}
                      label="Action Required"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="true">⚠️ Action Required</MenuItem>
                      <MenuItem value="false">No Action Required</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={searchSubmissions}
                      disabled={loading}
                    >
                      Apply Filters
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilters({
                          daysBack: 30,
                          senderEmail: '',
                          subjectContains: '',
                          submissionType: '',
                          hasAttachments: null,
                          actionRequired: null,
                        });
                        loadSubmissions();
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Hidden Admin Panel (Ctrl+Shift+A to toggle) */}
        {showAdminPanel && (
          <Accordion expanded sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}>
            <AccordionSummary>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <SettingsIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" color="warning.main">Admin Panel</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Press Ctrl+Shift+A to hide
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Cache Status Section */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <StorageIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Cache Status</Typography>
                      </Box>
                      {cacheStatus ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip 
                              size="small" 
                              label={isFromCache ? 'Cached' : 'Fresh'} 
                              color={isFromCache ? 'success' : 'primary'} 
                              icon={<CachedIcon />}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {cacheStatus.cached_entries} cached entries
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {cacheStatus.cache_duration_minutes}min cache duration
                          </Typography>
                          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexDirection: 'column' }}>
                            <Button
                              size="small"
                              startIcon={<RefreshIcon />}
                              onClick={warmCache}
                              disabled={loading}
                              variant="outlined"
                            >
                              {loading ? 'Warming...' : 'Warm Cache'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<SettingsBackupRestoreIcon />}
                              onClick={resetConnections}
                            >
                              Reset Connections
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<StorageIcon />}
                              onClick={() => syncFullHistory(5)}
                              disabled={historicalSyncing}
                            >
                              {historicalSyncing ? 'Syncing History...' : 'Sync Full History (5 years)'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CachedIcon />}
                              onClick={autoSyncNew}
                            >
                              Auto-Sync New (7 days)
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<RefreshIcon />}
                              onClick={updateTags}
                            >
                              Update Action Tags
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="info"
                              startIcon={<AutorenewIcon />}
                              onClick={autoUpdateAllTags}
                              title="Auto-update action required flags for all threads based on latest emails"
                            >
                              Auto-Update All Tags
                            </Button>
                            <Button
                              size="small"
                              variant={pollingEnabled ? "contained" : "outlined"}
                              color={pollingEnabled ? "success" : "primary"}
                              startIcon={pollingEnabled ? <NotificationsActiveIcon /> : <NotificationsIcon />}
                              onClick={togglePolling}
                              title="Enable/disable real-time polling for new emails"
                            >
                              {pollingEnabled ? 'Polling ON' : 'Enable Polling'}
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <CircularProgress size={24} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Debug Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <BugReportIcon color="secondary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Debug Info</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Total Submissions:</strong> {totalCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Search Period:</strong> {searchPeriodDays} days
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Data Source:</strong> {isFromCache ? 'Cache' : 'Live API'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Last Updated:</strong> {lastUpdated ? formatDate(lastUpdated) : 'Never'}
                      </Typography>
                      {cacheStatus && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Cache Entries: {cacheStatus.cache_entries.join(', ')}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Admin Panel Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <SettingsIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Admin Panel</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Administrative functions for data management and debugging.
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          🔄 Auto-Update System
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Action Required flags are automatically updated when:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, mb: 1 }}>
                          <li>New emails are synced (Auto-Sync New button)</li>
                          <li>Thread content is viewed (triggers auto-update for that thread)</li>
                          <li>Manual update is triggered (buttons below)</li>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Flags are automatically cleared when LGA responds to LSS requests.
                        </Typography>
                      </Box>
                      
                      {healthData ? (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            Health Percentage: {Math.round(healthData.health_percentage)}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {healthData.connected_accounts} of {healthData.total_accounts} accounts connected
                          </Typography>
                        </Box>
                      ) : (
                        <CircularProgress size={24} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Cache Status Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <StorageIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Cache Status</Typography>
                      </Box>
                      {cacheStatus ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Chip 
                              size="small" 
                              label={isFromCache ? 'Cached' : 'Fresh'} 
                              color={isFromCache ? 'success' : 'primary'} 
                              icon={<CachedIcon />}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {cacheStatus.cached_entries} cached entries
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {cacheStatus.cache_duration_minutes}min cache duration
                          </Typography>
                        </Box>
                      ) : (
                        <CircularProgress size={24} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Real-time Notification */}
        <Snackbar
          open={!!realtimeNotification}
          autoHideDuration={5000}
          onClose={() => setRealtimeNotification(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity="info" onClose={() => setRealtimeNotification(null)}>
            🔄 {realtimeNotification}
          </Alert>
        </Snackbar>

        {/* Loading Alert for Large Date Ranges */}
        {loading && filters.daysBack > 90 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Searching {filters.daysBack} days of emails across multiple accounts. This may take up to 1-2 minutes...
            </Box>
          </Alert>
        )}

        {/* Results Summary */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="body1">
              Showing {sortedThreadGroups.length} email threads 
              {(() => {
                const filteredCount = submissions.filter(submission => 
                  !submission.subject.toLowerCase().includes('confirmation receipt')
                ).length;
                const excludedCount = submissions.length - filteredCount;
                const actionRequiredCount = threadGroups.filter(thread => 
                  thread.tags.includes('action-required')
                ).length;
                
                let summary = excludedCount > 0 
                  ? `(${filteredCount} messages, ${excludedCount} confirmation receipts hidden)`
                  : `(${submissions.length} total messages)`;
                
                if (actionRequiredCount > 0) {
                  summary += ` • ${actionRequiredCount} require action`;
                }
                
                return summary;
              })()}
              {filters.subjectContains && (
                <Chip 
                  label={`Searching: "${filters.subjectContains}"`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 1 }}
                  onDelete={() => {
                    setFilters(prev => ({ ...prev, subjectContains: '' }));
                    loadSubmissions();
                  }}
                />
              )}
              {filters.actionRequired === true && (
                <Chip 
                  label="⚠️ Action Required Only" 
                  size="small" 
                  color="error" 
                  variant="filled"
                  sx={{ ml: 1 }}
                  onDelete={() => {
                    setFilters(prev => ({ ...prev, actionRequired: null }));
                    searchSubmissions();
                  }}
                />
              )}
            </Typography>
            {lastUpdated && (
              <Typography variant="body2" color="text.secondary">
                Last updated: {formatDate(lastUpdated)}
              </Typography>
            )}
          </Box>
          {filters.subjectContains && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setFilters(prev => ({ ...prev, subjectContains: '' }));
                loadSubmissions();
              }}
            >
              Clear Search
            </Button>
          )}
        </Box>

        {/* Submissions Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'date'}
                      direction={sortBy === 'date' ? sortDirection : 'asc'}
                      onClick={() => handleSort('date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'sender_name'}
                      direction={sortBy === 'sender_name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('sender_name')}
                    >
                      Participants
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'subject'}
                      direction={sortBy === 'subject' ? sortDirection : 'asc'}
                      onClick={() => handleSort('subject')}
                    >
                      Subject
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Types</TableCell>
                  <TableCell align="center">Attachments</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : sortedThreadGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No email threads found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedThreadGroups.map((threadGroup) => (
                    <TableRow key={threadGroup.thread_id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(threadGroup.latest_date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {threadGroup.account_emails.join(', ')}
                        </Typography>
                        {threadGroup.message_count > 1 && (
                          <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                            {threadGroup.message_count} messages
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {threadGroup.participants.slice(0, 2).join(', ')}
                        </Typography>
                        {threadGroup.participants.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{threadGroup.participants.length - 2} more
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {threadGroup.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {threadGroup.submission_types.slice(0, 2).map((type, index) => (
                            <Chip
                              key={index}
                              label={type}
                              color={getSubmissionTypeColor(type)}
                              size="small"
                            />
                          ))}
                          {threadGroup.submission_types.length > 2 && (
                            <Chip 
                              label={`+${threadGroup.submission_types.length - 2}`} 
                              size="small" 
                              variant="outlined" 
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Badge badgeContent={threadGroup.total_attachments} showZero color="primary">
                          <AttachFileIcon color={threadGroup.has_attachments ? 'primary' : 'disabled'} />
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {/* Prioritize action-required tags to show first */}
                          {(() => {
                            const actionTags = threadGroup.tags.filter(tag => tag === 'action-required');
                            const urgentTags = threadGroup.tags.filter(tag => tag === 'urgent');
                            const otherTags = threadGroup.tags.filter(tag => tag !== 'action-required' && tag !== 'urgent');
                            const prioritizedTags = [...actionTags, ...urgentTags, ...otherTags];
                            
                            return prioritizedTags.slice(0, 2).map((tag: string, index: number) => {
                              const tagProps = getTagProps(tag);
                              return (
                                <Chip 
                                  key={index} 
                                  label={tagProps.label}
                                  color={tagProps.color}
                                  variant={tagProps.variant}
                                  size="small" 
                                  sx={tagProps.sx}
                                />
                              );
                            });
                          })()}
                          {threadGroup.tags.length > 2 && (
                            <Chip label={`+${threadGroup.tags.length - 2}`} size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => {
                            // Use the latest message from the thread for compatibility with existing dialog
                            const latestMessage = threadGroup.messages.reduce((latest, msg) => 
                              msg.timestamp > latest.timestamp ? msg : latest
                            );
                            setSelectedSubmission(latestMessage);
                            setDetailDialogOpen(true);
                            loadThreadMessages(latestMessage);
                          }}
                        >
                          View Thread
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => {
            setDetailDialogOpen(false);
            setThreadMessages([]);
            setSelectedSubmission(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                Email Thread {threadMessages.length > 1 && `(${threadMessages.length} messages)`}
              </Typography>
              <Box>
                <Tooltip title="Refresh thread">
                  <IconButton 
                    onClick={() => selectedSubmission && loadThreadMessages(selectedSubmission)}
                    disabled={loadingThread}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                                 <IconButton onClick={() => {
                   setDetailDialogOpen(false);
                   setThreadMessages([]);
                   setSelectedSubmission(null);
                 }}>
                   ×
                 </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedSubmission && (
              <Box>
                {/* Thread Info */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>{selectedSubmission.subject}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">Account</Typography>
                  <Typography variant="body2">{selectedSubmission.account_email}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Thread Messages */}
                {loadingThread ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>Loading thread...</Typography>
                  </Box>
                ) : threadMessages.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Email Thread ({threadMessages.length} messages)
                    </Typography>
                    {threadMessages.map((message, index) => (
                      <Accordion key={message.id} defaultExpanded={index === 0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {(() => {
                                    const logo = getOrganizationLogo(message.sender_name, message.sender_email);
                                    return logo ? (
                                      <img 
                                        src={logo} 
                                        alt={getCleanSenderName(message.sender_name, message.sender_email)}
                                        style={{ 
                                          width: '16px', 
                                          height: '16px', 
                                          objectFit: 'contain' 
                                        }}
                                      />
                                    ) : null;
                                  })()}
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {getCleanSenderName(message.sender_name, message.sender_email)}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {formatDate(message.date)}
                                </Typography>
                                
                                {/* Recipient Information */}
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    <strong>From:</strong> {message.account_email}
                                  </Typography>
                                  
                                  {message.to_recipients && message.to_recipients.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      <strong>To:</strong> {formatRecipientList(message.to_recipients)}
                                    </Typography>
                                  )}
                                  
                                  {message.cc_recipients && message.cc_recipients.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      <strong>CC:</strong> {formatRecipientList(message.cc_recipients)}
                                    </Typography>
                                  )}
                                  
                                  {message.bcc_recipients && message.bcc_recipients.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      <strong>BCC:</strong> {formatRecipientList(message.bcc_recipients)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {message.has_attachments && (
                                <AttachFileIcon color="primary" fontSize="small" />
                              )}
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box>
                            {/* Message Content */}
                            {message.body_text ? (
                              <Box sx={{ mb: 2 }}>
                                <Paper sx={{ 
                                  p: 2, 
                                  bgcolor: 'grey.50',
                                  maxHeight: '400px',
                                  overflow: 'auto'
                                }}>
                                  <Typography 
                                    variant="body2" 
                                    style={{ 
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.6
                                    }}
                                  >
                                    {message.body_text}
                                  </Typography>
                                </Paper>
                              </Box>
                            ) : message.snippet ? (
                              <Box sx={{ mb: 2 }}>
                                <Paper sx={{ 
                                  p: 2, 
                                  bgcolor: 'grey.50',
                                  maxHeight: '400px',
                                  overflow: 'auto'
                                }}>
                                  <Typography 
                                    variant="body2" 
                                    style={{ 
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.6
                                    }}
                                  >
                                    {message.snippet}
                                  </Typography>
                                </Paper>
                              </Box>
                            ) : (
                              <Box sx={{ mb: 2 }}>
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic' }}>
                                    No message content available
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            {/* Message Attachments */}
                            {message.attachments && message.attachments.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  Attachments ({message.attachments.length})
                                </Typography>
                                <List dense>
                                  {message.attachments.map((attachment, attIndex) => (
                                    <ListItem key={attIndex}>
                                      <ListItemIcon>
                                        {getFileTypeIcon(attachment.file_type)}
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={attachment.filename}
                                        secondary={`${attachment.file_type} • ${attachment.size_formatted || 'Unknown size'}`}
                                      />
                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="Preview attachment">
                                          <IconButton
                                            onClick={() => previewAttachmentFile(selectedSubmission, attachment)}
                                            size="small"
                                          >
                                            <VisibilityIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download attachment">
                                          <IconButton
                                            onClick={() => downloadAttachment(selectedSubmission, attachment)}
                                            size="small"
                                          >
                                            <DownloadIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No thread messages available. This might be a single email or thread loading failed.
                    </Typography>
                    {selectedSubmission.body_preview && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Message Preview
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                            {selectedSubmission.body_preview}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDetailDialogOpen(false);
              setThreadMessages([]);
              setSelectedSubmission(null);
            }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Attachment Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={closePreview}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">
                  {previewAttachment?.filename}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {previewAttachment?.file_type} • {previewAttachment?.size_formatted}
                </Typography>
              </Box>
              <IconButton onClick={closePreview}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 1 }}>
            {previewLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading preview...</Typography>
              </Box>
            ) : (
              renderAttachmentPreview()
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => selectedSubmission && previewAttachment && downloadAttachment(selectedSubmission, previewAttachment)}
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
            <Button onClick={closePreview}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TestSheetSubmissions; 